/* global $ */
import { cleanupBootstrapModalBody } from './bootstrap-modal-cleanup.js';
import { resolveShowAlertMsg } from './resolve-show-alert-msg.js';

let ajaxModalRequestId = 0;
let ajaxModalXhr = null;

function notifyAjaxModalError(message) {
    const showAlertMsg = resolveShowAlertMsg();
    if (typeof showAlertMsg === 'function') {
        showAlertMsg(message, 'danger');
        return;
    }
    window.alert(message);
}

export function cancelAjaxModalLoad() {
    ajaxModalRequestId += 1;
    if (ajaxModalXhr) {
        ajaxModalXhr.abort();
        ajaxModalXhr = null;
    }
    cleanupBootstrapModalBody();
}

function disposeModalElement($modal) {
    if (!$modal || !$modal.length) {
        return;
    }
    $modal.off('.ajaxModalLoader');
    try {
        $modal.removeData('bs.modal');
    } catch (e) {}
}

function resetAjaxModalContainer($container) {
    if (ajaxModalXhr) {
        ajaxModalXhr.abort();
        ajaxModalXhr = null;
    }
    disposeModalElement($container.find('.modal').first());
    cleanupBootstrapModalBody();
    $container.empty();
}

function classListKeepingModalOpen(className) {
    return String(className || 'modal fade app-modal')
        .replace(/\b(show|in)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() + ' show';
}

function syncOpenBootstrapModalState($modal) {
    const modalInstance = $modal.data('bs.modal');
    if (!modalInstance) {
        return;
    }
    modalInstance._isShown = true;
    modalInstance._isTransitioning = false;
}

function moveCompanionNodes($parsed, $container, modalId) {
    $parsed.children().each(function () {
        if (this.nodeType !== 1) {
            return;
        }
        if (this.id && this.id === modalId) {
            return;
        }
        if ($(this).hasClass('modal')) {
            return;
        }
        if (this.id) {
            $container.find('#' + this.id).remove();
        }
        $container.append(this);
    });
}

function restoreModalDismissBehavior($modal, $sourceModal) {
    const backdropAttr = $sourceModal.attr('data-backdrop');
    const keyboardAttr = $sourceModal.attr('data-keyboard');

    if (backdropAttr === undefined) {
        $modal.removeAttr('data-backdrop');
    } else {
        $modal.attr('data-backdrop', backdropAttr);
    }
    if (keyboardAttr === undefined) {
        $modal.removeAttr('data-keyboard');
    } else {
        $modal.attr('data-keyboard', keyboardAttr);
    }

    const modalInstance = $modal.data('bs.modal');
    if (modalInstance && modalInstance._config) {
        modalInstance._config.backdrop = backdropAttr === undefined ? true : (backdropAttr === 'static' ? 'static' : backdropAttr !== 'false');
        modalInstance._config.keyboard = keyboardAttr === undefined ? true : keyboardAttr !== 'false';
    }
}

function fillOpenAjaxModal($modal, $container, html) {
    const modalId = String($modal.attr('id') || '');
    if (!modalId) {
        return false;
    }

    const $parsed = $('<div>').append($.parseHTML(html, document, true));
    const $sourceModal = $parsed.find('#' + modalId);
    if (!$sourceModal.length) {
        return false;
    }

    $modal.off('.ajaxModalLoader');
    $modal
        .removeAttr('data-ajax-modal-loading')
        .attr('class', classListKeepingModalOpen($sourceModal.attr('class')))
        .attr('aria-hidden', 'false');
    const sourceMemberId = $sourceModal.attr('data-member-id');
    if (sourceMemberId) {
        $modal.attr('data-member-id', sourceMemberId);
    }
    $modal.empty().append($sourceModal.contents());

    restoreModalDismissBehavior($modal, $sourceModal);
    syncOpenBootstrapModalState($modal);
    moveCompanionNodes($parsed, $container, modalId);
    return true;
}

function reportAjaxModalFailure(options, xhr) {
    if (typeof options.onError === 'function') {
        options.onError(xhr);
        return;
    }
    notifyAjaxModalError(options.loadErrorMessage || 'Unable to load. Please try again.');
}

export function openAjaxModal($container, options) {
    const opts = options || {};

    if (!$container || !$container.length) {
        notifyAjaxModalError(
            opts.missingContainerMessage
                || opts.missingTemplateMessage
                || opts.loadErrorMessage
                || 'This dialog is not available on this page. Please refresh and try again.'
        );
        return;
    }

    const templateId = String(opts.templateId || '');
    const tpl = templateId ? document.getElementById(templateId) : null;
    if (!tpl || !tpl.content) {
        notifyAjaxModalError(
            opts.missingTemplateMessage || 'This dialog is not available on this page. Please refresh and try again.'
        );
        return;
    }

    if (!opts.url) {
        notifyAjaxModalError(opts.loadErrorMessage || 'Unable to load. Please try again.');
        return;
    }

    ajaxModalRequestId += 1;
    const requestId = ajaxModalRequestId;
    resetAjaxModalContainer($container);
    $container.append(tpl.content.cloneNode(true));

    const $modal = $container.find('.modal[data-ajax-modal-loading], .modal').first();
    if (!$modal.length) {
        resetAjaxModalContainer($container);
        notifyAjaxModalError(
            opts.missingTemplateMessage || 'This dialog is not available on this page. Please refresh and try again.'
        );
        return;
    }

    let shellShown = false;
    let pendingHtml = null;

    function isActiveLoadingShell() {
        return requestId === ajaxModalRequestId && $modal.attr('data-ajax-modal-loading') === '1';
    }

    function cancelActiveLoadingShell() {
        if (!isActiveLoadingShell()) {
            return false;
        }
        pendingHtml = null;
        ajaxModalRequestId += 1;
        if (ajaxModalXhr) {
            ajaxModalXhr.abort();
            ajaxModalXhr = null;
        }
        return true;
    }

    function applyLoadedHtml(html) {
        if (!isActiveLoadingShell()) {
            return;
        }
        if (!fillOpenAjaxModal($modal, $container, html)) {
            ajaxModalRequestId += 1;
            resetAjaxModalContainer($container);
            reportAjaxModalFailure(opts);
            return;
        }
        if (typeof opts.afterFill === 'function' && opts.afterFill($container, $modal) === false) {
            ajaxModalRequestId += 1;
            resetAjaxModalContainer($container);
            reportAjaxModalFailure(opts);
            return;
        }
        $modal.trigger('shown.bs.modal');
        if (typeof opts.onReady === 'function') {
            opts.onReady($container, $modal);
        }
    }

    $modal.on('hide.bs.modal.ajaxModalLoader', function () {
        cancelActiveLoadingShell();
    });

    $modal.on('hidden.bs.modal.ajaxModalLoader', function () {
        if ($modal.attr('data-ajax-modal-loading') !== '1') {
            return;
        }
        cancelActiveLoadingShell();
        resetAjaxModalContainer($container);
        if (typeof opts.onCancel === 'function') {
            opts.onCancel();
        }
    });

    $modal.on('shown.bs.modal.ajaxModalLoader', function () {
        if (!isActiveLoadingShell()) {
            return;
        }
        shellShown = true;
        if (pendingHtml === null) {
            return;
        }
        const html = pendingHtml;
        pendingHtml = null;
        applyLoadedHtml(html);
    });

    $modal.modal('show');

    ajaxModalXhr = $.ajax({
        url: opts.url,
        type: opts.method || 'GET',
        data: opts.data || {},
    });

    ajaxModalXhr
        .done(function (html) {
            ajaxModalXhr = null;
            if (!isActiveLoadingShell()) {
                return;
            }
            if (!shellShown) {
                pendingHtml = html;
                return;
            }
            applyLoadedHtml(html);
        })
        .fail(function (xhr, textStatus) {
            ajaxModalXhr = null;
            pendingHtml = null;
            if (textStatus === 'abort' || requestId !== ajaxModalRequestId) {
                return;
            }
            ajaxModalRequestId += 1;
            resetAjaxModalContainer($container);
            reportAjaxModalFailure(opts, xhr);
        });
}
